import { Injectable, inject } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation/navigation.types';
import { TranslocoService } from '@ngneat/transloco';
import { Subject, filter, take, takeUntil, timer } from 'rxjs';

// Type for navigation components
interface NavigationComponent {
    navigation: FuseNavigationItem[];
    refresh?: () => void;
}

@Injectable({ providedIn: 'root' })
export class FuseNavigationService {
    private _translocoService = inject(TranslocoService);
    private _componentRegistry: Map<string, NavigationComponent> = new Map<string, NavigationComponent>();
    private _navigationStore: Map<string, FuseNavigationItem[]> = new Map<
        string,
        any
    >();
    private _initialized = false;
    private _initializing = false;
    private _initAttempts = 0;
    private _maxInitAttempts = 10; // Maximum number of attempts
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor() {
        // Automatically initialize the service when it is injected
        this.initialize();
        
        // Schedule repeated translation attempts to ensure everything is translated
        this._scheduleTranslationAttempts();
    }

    /**
     * Initializes the service and ensures translations are available
     */
    initialize(): void {
        // Avoid reinitializing if already done or in progress
        if (this._initialized || this._initializing) {
            return;
        }

        this._initializing = true;

        // Subscribe to translation loading events
        this._translocoService.events$
            .pipe(
                // Filter to only capture 'translationLoadSuccess' events
                filter(event => event.type === 'translationLoadSuccess'),
                // Unsubscribe when the service is destroyed
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                // Translate all registered navigation components
                this.translateAllNavigationComponents();
                this._initialized = true;
            });

        // Initial translation if translations are already loaded
        if (this._translocoService.getTranslation(this._translocoService.getActiveLang())) {
            this.translateAllNavigationComponents();
            this._initialized = true;
        }

        // Subscribe to language changes
        this._translocoService.langChanges$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(lang => {
                // Translate all navigation components
                this.translateAllNavigationComponents();
            });
    }

    /**
     * Schedules repeated translation attempts to ensure everything is translated
     * even if components are registered later
     */
    private _scheduleTranslationAttempts(): void {
        // Try every 500ms
        const attemptInterval = 500;
        
        // Create a timer that runs every 500ms
        timer(0, attemptInterval)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                if (this._initAttempts >= this._maxInitAttempts) {
                    // Stop attempts after reaching the maximum number
                    return;
                }
                
                this._initAttempts++;
                
                // If at least one component is registered, translate all components
                if (this._componentRegistry.size > 0) {
                    this.translateAllNavigationComponents();
                }
            });
    }

    /**
     * Translates all registered navigation components
     */
    translateAllNavigationComponents(): void {
        // Iterate over all registered components and translate them
        this._componentRegistry.forEach((component, name) => {
            this.translateNavigationItems(name);
        });
    }

    /**
     * Register navigation component
     *
     * @param name
     * @param component
     */
    registerComponent(name: string, component: NavigationComponent): void {
        this._componentRegistry.set(name, component);
        
        // Translate titles after registering the component
        // if the service is already initialized
        if (this._initialized) {
            this.translateNavigationItems(name);
        }
    }

    /**
     * Deregister navigation component
     *
     * @param name
     */
    deregisterComponent(name: string): void {
        this._componentRegistry.delete(name);
    }

    /**
     * Get navigation component from the registry
     *
     * @param name
     */
    getComponent<T>(name: string): T {
        return this._componentRegistry.get(name) as unknown as T;
    }

    /**
     * Store the given navigation with the given key
     *
     * @param key
     * @param navigation
     */
    storeNavigation(key: string, navigation: FuseNavigationItem[]): void {
        // Add to the store
        this._navigationStore.set(key, navigation);
    }

    /**
     * Get navigation from storage by key
     *
     * @param key
     */
    getNavigation(key: string): FuseNavigationItem[] {
        return this._navigationStore.get(key) ?? [];
    }

    /**
     * Delete the navigation from the storage
     *
     * @param key
     */
    deleteNavigation(key: string): void {
        // Check if the navigation exists
        if (!this._navigationStore.has(key)) {
            console.warn(
                `Navigation with the key '${key}' does not exist in the store.`
            );
        }

        // Delete from the storage
        this._navigationStore.delete(key);
    }

    /**
     * Utility function that returns a flattened
     * version of the given navigation array
     *
     * @param navigation
     * @param flatNavigation
     */
    getFlatNavigation(
        navigation: FuseNavigationItem[],
        flatNavigation: FuseNavigationItem[] = []
    ): FuseNavigationItem[] {
        for (const item of navigation) {
            if (item.type === 'basic') {
                flatNavigation.push(item);
                continue;
            }

            if (
                item.type === 'aside' ||
                item.type === 'collapsable' ||
                item.type === 'group'
            ) {
                if (item.children) {
                    this.getFlatNavigation(item.children, flatNavigation);
                }
            }
        }

        return flatNavigation;
    }

    /**
     * Utility function that returns the item
     * with the given id from given navigation
     *
     * @param id
     * @param navigation
     */
    getItem(
        id: string,
        navigation: FuseNavigationItem[]
    ): FuseNavigationItem | null {
        for (const item of navigation) {
            if (item.id === id) {
                return item;
            }

            if (item.children) {
                const childItem = this.getItem(id, item.children);

                if (childItem) {
                    return childItem;
                }
            }
        }

        return null;
    }

    /**
     * Utility function that returns the item's parent
     * with the given id from given navigation
     *
     * @param id
     * @param navigation
     * @param parent
     */
    getItemParent(
        id: string,
        navigation: FuseNavigationItem[],
        parent: FuseNavigationItem[] | FuseNavigationItem
    ): FuseNavigationItem[] | FuseNavigationItem | null {
        for (const item of navigation) {
            if (item.id === id) {
                return parent;
            }

            if (item.children) {
                const childItem = this.getItemParent(id, item.children, item);

                if (childItem) {
                    return childItem;
                }
            }
        }

        return null;
    }
    
    /**
     * Translates all items of a navigation component
     * 
     * @param componentName The name of the navigation component
     */
    translateNavigationItems(componentName: string): void {
        // Retrieve the navigation component
        const component = this._componentRegistry.get(componentName);
        
        // Check if the component exists
        if (!component || !component.navigation) {
            console.warn(`Navigation component '${componentName}' not found or has no navigation items.`);
            return;
        }
        
        // Translate each navigation item
        this.translateItems(component.navigation);
        
        // Refresh the component if it has a refresh method
        if (component.refresh && typeof component.refresh === 'function') {
            component.refresh();
        }
    }
    
    /**
     * Recursively translates all navigation items
     * 
     * @param items Array of navigation items
     */
    private translateItems(items: FuseNavigationItem[]): void {
        items.forEach(item => {
            // Translate the title if the item has an id
            if (item.id) {
                const path = `navigation.${item.id}.title`;
                this._translocoService.selectTranslate(path).pipe(take(1))
                    .subscribe(translation => {
                        if (translation) {
                            // Store the old value for debugging
                            const oldTitle = item.title;
                            
                            // Set the new translation
                            item.title = translation;
                            
                        } else {
                            console.warn(`No translation found for '${path}'`);
                        }
                    });
            }
            
            // Recursively translate children
            if (item.children && item.children.length > 0) {
                this.translateItems(item.children);
            }
        });
    }

    /**
     * Cleans up subscriptions and allocated resources
     */
    destroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}
